/**
 * AGRI-OPERATIONAL-SECTOR-R2 — shared live farm read for the operational views.
 *
 * The Dashboard and the Sector Detail must observe the SAME farm state through
 * the SAME reads, otherwise the two surfaces can disagree about which nodes
 * belong to a sector. This hook owns that read:
 *
 *   - topology (once)            → /iot/client-topology
 *   - Field/Sector hierarchy (once) → /iot/fields?farm_id= , /iot/sectors
 *   - nodes (~30s poll, no overlap) → /iot/nodes?farm_id=
 *
 * Fail-closed: a failed field/sector read yields an EMPTY placement map, so no
 * node can be attributed to a sector by guesswork — every sector degrades to
 * "not commissioned" rather than showing another sector's data.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CgmTopology } from '../config/cgmPilot';
import { iotApi, type IoTNode } from '../lib/iotApi';
import {
  buildSectorPlacement,
  type FieldRecord,
  type SectorPlacement,
  type SectorRecord,
} from '../lib/operationalOverview';

export const NODES_POLL_MS = 30000;

export interface FarmOperationalData {
  topology: CgmTopology | null;
  nodes: IoTNode[];
  nodesLoaded: boolean;
  /** True only after /fields and /sectors have completed successfully or failed explicitly. */
  placementLoaded: boolean;
  /** True when the hierarchy join could not be read; empty placement is then NOT authoritative. */
  placementError: boolean;
  placement: Map<string, SectorPlacement>;
  loading: boolean;
  loadError: boolean;
  /** Re-read nodes now and resolve with the fresh list (used for command proofs). */
  refreshNodes: () => Promise<IoTNode[]>;
}

export function useFarmOperationalData(): FarmOperationalData {
  const [topology, setTopology] = useState<CgmTopology | null>(null);
  const [fields, setFields] = useState<FieldRecord[]>([]);
  const [sectors, setSectors] = useState<SectorRecord[]>([]);
  const [nodes, setNodes] = useState<IoTNode[]>([]);
  const [nodesLoaded, setNodesLoaded] = useState(false);
  const [placementLoaded, setPlacementLoaded] = useState(false);
  const [placementError, setPlacementError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const nodesInFlightRef = useRef(false);
  const mountedRef = useRef(true);
  const farmIdRef = useRef<string>('');

  /**
   * Read the farm's nodes. Overlapping polls are suppressed, but a caller that
   * needs a PROVING read (post-command refresh) still receives the freshest
   * list — it resolves with the current snapshot rather than silently no-oping.
   */
  const loadNodes = useCallback(async (farmId: string): Promise<IoTNode[]> => {
    if (nodesInFlightRef.current) return nodes;
    nodesInFlightRef.current = true;
    try {
      const nodeRows = await iotApi.getJson<IoTNode[]>(
        `/iot/nodes?farm_id=${encodeURIComponent(farmId)}`,
      );
      if (!mountedRef.current) return nodeRows;
      setNodes(nodeRows);
      setNodesLoaded(true);
      setLoadError(false);
      return nodeRows;
    } catch (err) {
      if (mountedRef.current) {
        setNodes([]);
        setNodesLoaded(false);
        setLoadError(true);
      }
      throw err;
    } finally {
      nodesInFlightRef.current = false;
    }
  }, [nodes]);

  const loadNodesRef = useRef(loadNodes);
  loadNodesRef.current = loadNodes;

  useEffect(() => {
    mountedRef.current = true;
    let active = true;

    const loadTopology = async (): Promise<void> => {
      const topologyRow = await iotApi.getJson<CgmTopology>('/iot/client-topology');
      if (!active) return;
      setTopology(topologyRow);
      farmIdRef.current = topologyRow.farmId;

      try {
        const [fieldRows, sectorRows] = await Promise.all([
          iotApi.getJson<FieldRecord[]>(
            `/iot/fields?farm_id=${encodeURIComponent(topologyRow.farmId)}`,
          ),
          iotApi.getJson<SectorRecord[]>('/iot/sectors'),
        ]);
        if (!active) return;
        setFields(fieldRows);
        setSectors(sectorRows);
        setPlacementLoaded(true);
        setPlacementError(false);
      } catch {
        // Fail closed: no placement rather than a guessed association.
        if (active) {
          setFields([]);
          setSectors([]);
          setPlacementLoaded(true);
          setPlacementError(true);
        }
      }
    };

    loadTopology()
      .catch(() => {
        if (active) setLoadError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
      mountedRef.current = false;
    };
  }, []);

  // Node poll, once the farm id is known.
  useEffect(() => {
    const farmId = topology?.farmId;
    if (!farmId) return;
    loadNodesRef.current(farmId).catch(() => undefined);
    const interval = window.setInterval(() => {
      loadNodesRef.current(farmId).catch(() => undefined);
    }, NODES_POLL_MS);
    return () => window.clearInterval(interval);
  }, [topology?.farmId]);

  const placement = useMemo(
    () => (topology ? buildSectorPlacement(fields, sectors, topology.sites) : new Map()),
    [topology, fields, sectors],
  );

  const refreshNodes = useCallback(async (): Promise<IoTNode[]> => {
    const farmId = farmIdRef.current;
    if (!farmId) return [];
    return loadNodesRef.current(farmId);
  }, []);

  return { topology, nodes, nodesLoaded, placementLoaded, placementError, placement, loading, loadError, refreshNodes };
}
